import { useEffect, useRef, useState } from 'react';
import type { DistributionList } from '../lib/api';

interface Props {
  lists: DistributionList[];
  value: string[];
  onChange: (ids: string[]) => void;
  emptyHint?: string;
}

export function ListMultiPicker({ lists, value, onChange, emptyHint }: Props) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const valueSet = new Set(value);
  const selected = lists.filter((l) => valueSet.has(l.id));

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  function toggle(id: string) {
    if (valueSet.has(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  }

  return (
    <div ref={root} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="input text-left flex items-center justify-between gap-2"
      >
        <span className={selected.length ? 'text-slate-900' : 'text-slate-500'}>
          {selected.length === 0
            ? lists.length === 0
              ? emptyHint ?? 'No distribution lists yet'
              : 'No lists selected'
            : selected.map((l) => l.name).join(', ')}
        </span>
        <svg className="w-4 h-4 text-slate-500" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.3 7.3a1 1 0 011.4 0L10 10.6l3.3-3.3a1 1 0 111.4 1.4l-4 4a1 1 0 01-1.4 0l-4-4a1 1 0 010-1.4z" clipRule="evenodd" />
        </svg>
      </button>
      {open && lists.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-auto">
          {lists.map((l) => (
            <label
              key={l.id}
              className="flex items-start gap-2 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={valueSet.has(l.id)}
                onChange={() => toggle(l.id)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="text-slate-900">{l.name}</div>
                {l.description && (
                  <div className="text-xs text-slate-500 truncate">{l.description}</div>
                )}
                <div className="text-xs text-slate-500">
                  {l.members.length} {l.members.length === 1 ? 'member' : 'members'}
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
